import {
  PageStructure,
  SelectedElementContainer,
  toDragPointerEvent,
  ToolMessage,
  useDocumentViewer,
  useDocumentViewerState,
  useLinearTransform,
  ViewElementContainer
} from '@st/document-viewer-kit'
import {
  AnnotationBase,
  ElementStatus,
  selAnnotations,
  selNotYetCreatedAnnotationDraftIds
} from '@st/document-viewer-kit/src/document-viewer-module'
import { useUpdateBounds } from '@st/document-viewer-kit'
import type { CommentAnnotation } from '@st/sdk'
import {
  CommentCard,
  CommentComposer,
  ContextMenu,
  ContextMenuItem,
  PencilIcon,
  TrashIcon
} from '@st/theme'
import { cuid } from '@st/util/cuid'
import { asc, levels, sort } from '@st/util/sort'
import { formatISO8601Date } from '@st/util/time'
import { CommentToolState } from '../../annotations'
import { useAnnotationsContext } from '../../annotations-context'

const COMMENT_SIZE = 20

export function CommentToolOverlay({
  page,
  toolState,
  activeAnnotation,
  status,
  send
}: {
  page: PageStructure
  toolState: CommentToolState
  activeAnnotation: CommentAnnotation | undefined
  status: ElementStatus
  send: (message: ToolMessage<CommentAnnotation, CommentToolState>) => void
}) {
  const { currentUser } = useAnnotationsContext()

  const transform = useLinearTransform()

  return (
    <div
      style={{
        position: 'absolute',
        cursor: 'copy',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.0)'
      }}
      onPointerDown={(e) => {
        e.preventDefault()
        e.stopPropagation()

        const pointerEvent = toDragPointerEvent(e, transform, 'drawing-canvas')

        if (activeAnnotation) {
          send({ type: 'deselectAnnotation' })
          return
        } else {
          send({
            type: 'createAnnotationDraft',
            elementStatus: 'editing',
            annotationDraft: {
              id: cuid(),
              time: new Date().toISOString(),
              authorId: currentUser?.id,
              type: 'comment',
              pageNumber: page.pageNumber,
              content: '',
              bounds: {
                x: pointerEvent.point.x,
                y: pointerEvent.point.y,
                width: COMMENT_SIZE,
                height: COMMENT_SIZE
              }
            }
          })
        }
      }}
    />
  )
}

export function CommentAnnotationElement({
  annotation,
  page,
  status,
  send
}: {
  annotation: CommentAnnotation
  page: PageStructure
  status: ElementStatus
  send: (message: ToolMessage<CommentAnnotation, CommentToolState>) => void
}) {
  switch (status) {
    case 'selected':
    case 'editing':
      return <CommentAnnotationSelected annotation={annotation} send={send} />
    case 'none':
      return (
        <CommentAnnotationView
          annotation={annotation}
          onSelect={() => send({ type: 'selectAnnotation', annotationId: annotation.id })}
        />
      )
  }
}

function CommentAnnotationSelected({
  annotation,
  send
}: {
  annotation: CommentAnnotation
  send: (message: ToolMessage<CommentAnnotation, CommentToolState>) => void
}) {
  const { onMove, onResize } = useUpdateBounds({
    initialBounds: annotation.bounds,
    onUpdate: (event) => {
      send({
        type: 'updateAnnotationDraft',
        annotationDraft: { ...annotation, bounds: event.newBounds }
      })
    },
    onCommit: (event) => {
      send({
        type: 'updateAnnotationDraft',
        annotationDraft: { ...annotation, bounds: event.newBounds }
      })
    }
  })

  return (
    <SelectedElementContainer
      bounds={annotation.bounds}
      onMove={onMove}
      onResize={onResize}
      resizable={false}
    >
      <CommentAnnotationDisplay annotation={annotation} />
    </SelectedElementContainer>
  )
}

function CommentAnnotationView({
  annotation,
  onSelect
}: {
  annotation: CommentAnnotation
  onSelect: () => void
}) {
  return (
    <ViewElementContainer key={annotation.id} bounds={annotation.bounds} onClick={onSelect}>
      <CommentAnnotationDisplay annotation={annotation} />
    </ViewElementContainer>
  )
}

function CommentAnnotationDisplay({}: { annotation: CommentAnnotation }) {
  return <NoteIcon color="#FFCD46" />

  function NoteIcon({ color }: { color: string }) {
    return (
      <div style={{ width: 20, height: 20 }}>
        <svg style={{ color: color }} width="20" height="20" viewBox="0 0 38 38" fill="none">
          <rect width="38" height="38" fill="currentColor" />
          <path d="M31 7H7V26H14.5V32L21 26H31V7Z" fill="white" />
          <rect x="10" y="14" width="17" height="1.5" fill="currentColor" />
          <rect x="10" y="18" width="14" height="1.5" fill="currentColor" />
        </svg>
      </div>
    )
  }
}

export function CommentsSidebar() {
  const documentViewer = useDocumentViewer()

  const comments = useDocumentViewerState((s) =>
    sort(
      Object.values(selAnnotations(s, isCommentAnnotation)),
      levels(
        asc((a) => a.bounds.y),
        asc((a) => a.bounds.x)
      )
    )
  )
  const selectionState = useDocumentViewerState((s) => s.selectionState)

  const notYetCreatedAnnotationDraftIds = useDocumentViewerState(selNotYetCreatedAnnotationDraftIds)

  const { users } = useAnnotationsContext()

  return (
    <div
      className="flex w-72 flex-col gap-4 overflow-y-scroll bg-stone-500 p-4"
      style={{ scrollbarWidth: 'none' }}
    >
      {comments.map((comment) => {
        const selectionStatus =
          selectionState.type != 'none' && selectionState.annotationId == comment.id
            ? selectionState.type
            : 'none'

        const user = users.find((u) => u.id == comment.authorId)
        const action = notYetCreatedAnnotationDraftIds.includes(comment.id) ? 'create' : 'update'

        if (selectionStatus == 'editing' && action == 'create') {
          return (
            <AddCommentComposer key={comment.id} comment={comment} send={documentViewer.send} />
          )
        } else if (selectionStatus == 'editing' && action == 'update') {
          return (
            <EditCommentComposer key={comment.id} comment={comment} send={documentViewer.send} />
          )
        }

        return (
          <CommentCard
            key={comment.id}
            title={user?.name ?? user?.email ?? ''}
            time={comment.time ? formatISO8601Date(comment.time) : ''}
            selected={selectionStatus == 'selected'}
            onClick={() => {
              documentViewer.send({ type: 'selectAnnotation', annotationId: comment.id })
            }}
            menu={
              <ContextMenu>
                <ContextMenuItem
                  icon={<PencilIcon className="size-3" />}
                  onClick={() =>
                    documentViewer.send({
                      type: 'beginEditingAnnotation',
                      annotationDraft: comment
                    })
                  }
                >
                  Edit
                </ContextMenuItem>
                <ContextMenuItem
                  icon={<TrashIcon className="size-3" />}
                  onClick={() =>
                    documentViewer.send({ type: 'deleteAnnotation', annotationId: comment.id })
                  }
                >
                  Delete
                </ContextMenuItem>
              </ContextMenu>
            }
          >
            {comment.content}
          </CommentCard>
        )
      })}
    </div>
  )
}

function AddCommentComposer({
  comment,
  send
}: {
  comment: CommentAnnotation
  send: (message: ToolMessage<CommentAnnotation, CommentToolState>) => void
}) {
  return (
    <CommentComposer
      autoFocus={true}
      placeholder="Write a comment..."
      value={comment.content}
      onChange={(value) => {
        send({ type: 'updateAnnotationDraft', annotationDraft: { ...comment, content: value } })
      }}
      onClickCancel={() => {
        send({ type: 'discardAnnotationDraft', annotationId: comment.id })
      }}
      onClickAdd={() => {
        send({ type: 'selectAnnotation', annotationId: comment.id })
        send({ type: 'createAnnotation', annotationDraft: comment })
      }}
    />
  )
}

function EditCommentComposer({
  comment,
  send
}: {
  comment: CommentAnnotation
  send: (message: ToolMessage<CommentAnnotation, CommentToolState>) => void
}) {
  return (
    <CommentComposer
      autoFocus={true}
      placeholder="Write a comment..."
      value={comment.content}
      onChange={(value) => {
        send({ type: 'updateAnnotationDraft', annotationDraft: { ...comment, content: value } })
      }}
      onClickCancel={() => {
        send({ type: 'discardAnnotationDraft', annotationId: comment.id })
      }}
      onClickAdd={() => {
        send({ type: 'selectAnnotation', annotationId: comment.id })
        send({ type: 'updateAnnotation', annotationDraft: comment })
      }}
    />
  )
}

export function isCommentAnnotation(annotation: AnnotationBase): annotation is CommentAnnotation {
  return annotation.type == 'comment'
}
